import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { AccountService } from 'src/app/private/services/account.service';
import { ProjectService } from 'src/app/private/services/project.service';
import { UsersService } from 'src/app/private/services/users.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { CompanyComponent } from '../../configuration/company/company.component';
import { CompanyService } from 'src/app/private/services/company.service';
import { AutoCompleteSearchComponent } from '../auto-complete-search/auto-complete-search.component';
import { debounceTime, distinctUntilChanged, switchMap, catchError } from 'rxjs/operators';
import { of, Subject } from 'rxjs';
import { appendCenterScopeToPayload } from 'src/app/utils/tenant-scope.util';


@Component({
  selector: 'app-project-form',
  templateUrl: './project-form.component.html',
  styleUrls: ['./project-form.component.scss']
})
export class ProjectFormComponent implements OnInit {

  @Input() editData: any = null;  // If null → Add Mode  
  @Output() formSubmit = new EventEmitter<any>();
  @Output() close = new EventEmitter();
 @ViewChild(AutoCompleteSearchComponent) childComponent!: AutoCompleteSearchComponent;
  projectForm!: FormGroup;
  formAction = "Add";

  projectManagers: any[] = [];
  memberDropdownSettings = {
    singleSelection: false,
    idField: 'item_id',
    textField: 'item_text',
    selectAllText: 'Select All',
    unSelectAllText: 'UnSelect All',
    itemsShowLimit: 3,
    allowSearchFilter: true,
  };
  selectedMembers: any[] = [];
  memberOptions: any[] = [];
  clients: any[] = [];
  @Input() btnLoading: boolean = false; 
  projectCategories = [
  "Software Development",
  "Web Development",
  "Mobile Development",
  "System Integration",
  "DevOps / Infrastructure",
  "IT Support / Maintenance",
  "Cloud Implementation",
  "Cybersecurity",
  "Data & Analytics",
  "AI / Machine Learning",
  "API Development",
  "Business Process Improvement",
  "Operations Optimization",
  "Automation",
  "Compliance / Governance",
  "Product Design",
  "Product Enhancement",
  "Prototype / MVP",
  "Research & Development (R&D)",
  "Digital Marketing",
  "Branding",
  "UI/UX Design",
  "Graphic Design",
  "CRM Customization",
  "ERP Module",
  "Finance Module",
  "HR Module",
  "Inventory Module",
  "Support / Bug Fixing",
  "Retainer Support",
  "Training / Enablement",
  "Healthcare Projects",
  "Education Projects",
  "E-Commerce Projects",
  "Logistics Projects",
  "Manufacturing Projects"
];
accounts: any[] = [];
selectedAccount:any;
@Input() control!: FormControl;
prefixError: string = '';
prefixCheckSubject = new Subject<string>();
isCheckingPrefix: boolean = false;

  // Custom Validators
  static plannedEndDateValidator(control: AbstractControl): ValidationErrors | null {
    const formGroup = control.parent;
    if (!formGroup) return null;

    const startDate = formGroup.get('start_date')?.value;
    const plannedEndDate = control.value;

    if (startDate && plannedEndDate) {
      const start = new Date(startDate);
      const end = new Date(plannedEndDate);
      
      if (end < start) {
        return { plannedEndBeforeStart: true };
      }
    }
    return null;
  }

  static deadlineValidator(control: AbstractControl): ValidationErrors | null {
    const formGroup = control.parent;
    if (!formGroup) return null;

    const startDate = formGroup.get('start_date')?.value;
    const plannedEndDate = formGroup.get('planned_end_date')?.value;
    const deadline = control.value;

    if (deadline) {
      const deadlineDate = new Date(deadline);
      
      // Check if deadline is before start date
      if (startDate) {
        const start = new Date(startDate);
        if (deadlineDate < start) {
          return { deadlineBeforeStart: true };
        }
      }
      
      // Check if deadline is before planned end date
      if (plannedEndDate) {
        const plannedEnd = new Date(plannedEndDate);
        if (deadlineDate < plannedEnd) {
          return { deadlineBeforePlannedEnd: true };
        }
      }
    }
    return null;
  }

  constructor(
    private fb: FormBuilder,
    private accountApi: CompanyService,
    private api: AccountService,
    private userApi: UsersService,
    private localStorage: LocalStorageService,
    private projectService: ProjectService
  ) {}
  selectAccount(acc: any) {
    this.control.setValue(acc.account_id);
  }
  closeForm() {
    this.close.emit();
  }

  /* -------------------- ON INIT --------------------- */
  ngOnInit(): void {
    this.createForm();
    this.loadProjectManagers();

    // Add listeners for date validation
    this.projectForm.get('start_date')?.valueChanges.subscribe(() => {
      this.projectForm.get('planned_end_date')?.updateValueAndValidity({ emitEvent: false });
      this.projectForm.get('deadline')?.updateValueAndValidity({ emitEvent: false });
    });

    this.projectForm.get('planned_end_date')?.valueChanges.subscribe(() => {
      this.projectForm.get('deadline')?.updateValueAndValidity({ emitEvent: false });
    });

    // Set up debounced prefix checking
    this.setupDebouncedPrefixCheck();

    // Add listener for project name to generate prefix automatically
    this.projectForm.get('name')?.valueChanges.subscribe((name) => {
      if (name && this.formAction === 'Add') {
        this.generatePrefixFromName(name);
        // Trigger debounced prefix check
        const generatedPrefix = this.extractPrefixFromName(name);
        if (generatedPrefix && generatedPrefix.length >= 3) {
          this.prefixCheckSubject.next(generatedPrefix);
        }
      }
    });

    // Clear prefix error when user manually types in prefix field
    this.projectForm.get('project_prefix')?.valueChanges.subscribe((prefix) => {
      this.prefixError = '';
      // Also check manually entered prefix
      if (prefix && prefix.length >= 3 && this.formAction === 'Add') {
        this.prefixCheckSubject.next(prefix);
      }
    });

    console.log("Edit Data Received:", this.editData);
    if (this.editData.project_id) {
      console.log("Edit Data Received:", this.editData);
      this.formAction = "Update";
      this.projectForm.patchValue(this.editData);
        // Patch modules (FormArray)
      this.setEditData(this.editData);
    }
  }

  // Setup debounced prefix uniqueness checking
  setupDebouncedPrefixCheck() {
    this.prefixCheckSubject.pipe(
      debounceTime(500), // Wait 500ms after user stops typing
      distinctUntilChanged(), // Only check if prefix actually changed
      switchMap((prefix: string) => {
        if (!prefix || prefix.length < 3) {
          return of(null);
        }

        this.isCheckingPrefix = true;
        const param = {
          client_id: this.localStorage.getClientId(),
          project_prefix: prefix.toUpperCase(),
          user_id: this.localStorage.getUserId(),
        };

        return this.projectService.getAllProject(param).pipe(
          catchError(() => of(null))
        );
      })
    ).subscribe({
      next: (res: any) => {
        this.isCheckingPrefix = false;
        
        if (res && res.rows) {
          const existingProjects = res.rows || [];
          const currentPrefix = this.projectForm.get('project_prefix')?.value?.toUpperCase();
          
          const duplicateExists = existingProjects.some((p: any) => 
            p.project_prefix?.toUpperCase() === currentPrefix
          );

          if (duplicateExists) {
            this.prefixError = 'This prefix is already in use. Please modify the project name or prefix.';
            this.projectForm.get('project_prefix')?.setErrors({ duplicate: true });
          } else {
            // Clear the duplicate error if it was set
            const currentErrors = this.projectForm.get('project_prefix')?.errors;
            if (currentErrors && currentErrors['duplicate']) {
              delete currentErrors['duplicate'];
              const hasOtherErrors = Object.keys(currentErrors).length > 0;
              this.projectForm.get('project_prefix')?.setErrors(hasOtherErrors ? currentErrors : null);
            }
            this.prefixError = '';
          }
        }
      },
      error: (e) => {
        this.isCheckingPrefix = false;
        console.error('Error checking prefix:', e);
      }
    });
  }

  // Helper method to extract prefix from name (without updating form)
  extractPrefixFromName(name: string): string {
    if (!name || name.trim().length === 0) return '';
    
    const cleanName = name.trim().replace(/[^a-zA-Z\s]/g, '');
    const words = cleanName.split(/\s+/).filter(word => word.length > 0);
    
    let prefix = '';
    
    if (words.length === 1) {
      prefix = words[0].substring(0, 5).toUpperCase();
    } else if (words.length === 2) {
      const firstPart = words[0].substring(0, 3);
      const secondPart = words[1].substring(0, 2);
      prefix = (firstPart + secondPart).toUpperCase();
    } else if (words.length === 3) {
      prefix = (words[0].substring(0, 2) + words[1].substring(0, 2) + words[2].charAt(0)).toUpperCase();
    } else {
      prefix = words.slice(0, 5).map(w => w.charAt(0)).join('').toUpperCase();
    }
    
    if (prefix.length < 3) {
      const firstWord = words[0];
      for (let i = prefix.length; i < 3 && i < firstWord.length; i++) {
        prefix += firstWord.charAt(i).toUpperCase();
      }
      while (prefix.length < 3) {
        prefix += 'X';
      }
    } else if (prefix.length > 5) {
      prefix = prefix.substring(0, 5);
    }
    
    return prefix;
  }

  // Generate prefix from project name
  generatePrefixFromName(name: string) {
    if (!name || name.trim().length === 0) return;
    
    // Remove special characters and extra spaces
    const cleanName = name.trim().replace(/[^a-zA-Z\s]/g, '');
    
    // Split into words
    const words = cleanName.split(/\s+/).filter(word => word.length > 0);
    
    let prefix = '';
    
    if (words.length === 1) {
      // Single word: take first 5 letters
      prefix = words[0].substring(0, 5).toUpperCase();
    } else if (words.length === 2) {
      // Two words: take first 2-3 letters from each word
      const firstPart = words[0].substring(0, 3);
      const secondPart = words[1].substring(0, 2);
      prefix = (firstPart + secondPart).toUpperCase();
    } else if (words.length === 3) {
      // Three words: take first 2 letters from first word, 2 from second, 1 from third
      prefix = (words[0].substring(0, 2) + words[1].substring(0, 2) + words[2].charAt(0)).toUpperCase();
    } else {
      // Four or more words: take first letter of first 5 words
      prefix = words.slice(0, 5).map(w => w.charAt(0)).join('').toUpperCase();
    }
    
    // Ensure prefix is at least 3 characters and at most 5
    if (prefix.length < 3) {
      // Pad with letters from the first word if needed
      const firstWord = words[0];
      for (let i = prefix.length; i < 3 && i < firstWord.length; i++) {
        prefix += firstWord.charAt(i).toUpperCase();
      }
      // If still less than 3, pad with 'X'
      while (prefix.length < 3) {
        prefix += 'X';
      }
    } else if (prefix.length > 5) {
      // Truncate to 5 characters
      prefix = prefix.substring(0, 5);
    }
    
    // Update the prefix field
    this.projectForm.patchValue({ project_prefix: prefix }, { emitEvent: false });
  }


 setEditData(data: any) {

  // Clear existing modules
  
  this.modules.clear();

  if (data.modules && data.modules.length > 0) {
    data.modules.forEach((m: any) => {
      this.modules.push(
        this.fb.group({
          module_id: [m.module_id],
          module_name: [m.module_name],
          description: [m.description]
        })
      );
    });
  }

  if (data.members && data.members.length > 0) {
    this.selectedMembers = data.members
      .filter((m: any) => m.user)
      .map((m: any) => ({
        item_id: m.user.user_id,
        item_text: `${m.user.fname || ''} ${m.user.lname || ''}`.trim(),
      }));
  } else {
    this.selectedMembers = [];
  }

   const convert = (d: any) =>
    d ? new Date(d).toISOString().substring(0, 10) : null;
  this.selectedAccount = data.account_id;
  this.projectForm.patchValue({
    planned_end_date: convert(data.planned_end_date),
    start_date: convert(data.start_date),
    deadline: convert(data.deadline),
    // Add other fields if needed
  });

}

  /* -------------------- FORM BUILDER --------------------- */
  createForm() {
    this.projectForm = this.fb.group({
      client_id: [this.localStorage.getClientId(), Validators.required],
      account_id: [''],

      name: ['', Validators.required],
      project_prefix: ['',  [
    Validators.required,
    Validators.minLength(3),
    Validators.maxLength(5),
    Validators.pattern(/^[A-Za-z]+$/)   // optional: allow only letters
  ]],
      description: [''],
      project_type: ['', Validators.required],
      category: [''],
      start_date: [null],
      planned_end_date: [null, [ProjectFormComponent.plannedEndDateValidator]],
      deadline: [null, [ProjectFormComponent.deadlineValidator]],
      status: ['', Validators.required],
      priority: ['', Validators.required],
      progress: [0],
      project_manager_id: [null],
      allow_bug_reporting: [true],
      allow_time_tracking: [true],
      allow_sprints: [false],
      allow_task_dependencies: [true],
      visibility: ['private'],
      modules: this.fb.array([])
    });
  }
  get modules(): FormArray {
  return this.projectForm.get('modules') as FormArray;
}

scrollToFirstError() {
  const firstInvalid: any = document.querySelector('.errors');

  if (firstInvalid) {
    firstInvalid.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });

    firstInvalid.focus();
  }
}
addModule() {
  const moduleGroup = this.fb.group({
    module_name: ['', Validators.required],
    description: ['']
  });

  this.modules.push(moduleGroup);
}
removeModule(index: number) {
  this.modules.removeAt(index);
}
  /* -------------------- LOAD MANAGERS --------------------- */
  loadProjectManagers() {
    const param = { client_id: this.localStorage.getClientId() };

    this.userApi.findAllUsers(param).subscribe({
      next: (res: any) => {
        this.projectManagers = res;
        this.memberOptions = this.mapUsersForMemberDropdown(res);
      },
      error: (e: any) => console.error(e)
    });
  }

  mapUsersForMemberDropdown(users: any[]) {
    return (users || []).map((user: any) => ({
      item_id: user.user_id,
      item_text: `${user.prefix || ''} ${user.fname || ''} ${user.lname || ''}`.replace(/\s+/g, ' ').trim(),
    }));
  }

  /* -------------------- SUBMIT FORM --------------------- */
  submitForm() {
    if (!this.projectForm.valid) {
      this.projectForm.markAllAsTouched();
      this.scrollToFirstError();
      return;
    }

    const payload = appendCenterScopeToPayload(
      {
        ...this.projectForm.value,
        account_id: this.childComponent.selectedId,
        member_user_ids: this.selectedMembers.map((item) => item.item_id),
        created_by: this.localStorage.getUserId(),
        user_id: this.localStorage.getUserId(),
        ...(this.editData ? { id: this.editData.id } : {}),
      },
      this.localStorage
    );

    // Form is already validated including prefix uniqueness check via debouncing
    console.log("Form Submitted:", payload);
    this.formSubmit.emit(payload);
  }
}
